import { CheckOutlined, CloseOutlined, DownOutlined, EditOutlined } from "@ant-design/icons"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { message as antdMessage, Button, Checkbox, Col, Dropdown, Input, Row, Tag } from "antd"
import { useEffect, useState } from "react"
import { SAMPLE_STATUS_COLORS } from "../config"
import { updateSampleName, updateSampleStatus } from "../endpoints"
import { SampleInterface, SampleStatusType } from "../types"


// Remove unused TextArea assignment
// const { TextArea } = Input

// Remove unused Modal methods
// const { success, warning, error, confirm } = Modal
// "new" | "received" | "running" | "completed" | "canceled" |"review-requested" | "review-completed" | "rerun-completed"
const SAMPLE_STATUS_DICT = {
    new: "New",
    completed: "Completed",
    received: "Received",
    running: "Running",
    canceled: "Canceled",
    "review-requested": "Review Requested",
    "review-completed": "Review Completed",
    "rerun-requested": "Rerun Requested",
    "rerun-completed": "Rerun Completed",
    "rerun-canceled": "Rerun Canceled",
    "": "Unknown",
}

const SAMPLE_STATUS_OPTIONS: { label: string; value: SampleStatusType }[] = [
    { label: "new", value: "new" },
    { label: "completed", value: "completed" },
    { label: "review-requested", value: "review-requested" },
    { label: "review-completed", value: "review-completed" },
    { label: "rerun-completed", value: "rerun-completed" },

    // Add more if needed
]
const MENU_WITH_COLORS=false
export function ViewSampleSequence({
    sample,
    admin = false,
}: {
    sample: SampleInterface | null
    admin?: boolean
}): JSX.Element {
    const [nbases, setNbases] = useState(0)
    const [requestReview, setRequestReview] = useState(false)
    const [messageApi, contextHolder] = antdMessage.useMessage()
    const queryClient = useQueryClient()
    const [editingName, setEditingName] = useState(false)
    const [nameValue, setNameValue] = useState(sample?.sampleName ?? '')
    // const { data, refetch } = useQuery({ queryKey: ["getorders", "one", sample?.id] })

    useEffect(() => {
        if (sample === null) {
            setNbases(0)
        } else {
            setNbases(sample?.sequence?.length ?? 0)
            setRequestReview(sample.status === "review-requested")
        }
    }, [sample?.sequence, sample, sample?.status])

    useEffect(() => {
        setNameValue(sample?.sampleName ?? '')
    }, [sample?.sampleName])

    const updateNameMutation = useMutation({
        mutationFn: (newName: string) =>
            updateSampleName({ sampleId: sample?.id ?? 0, sampleName: newName }),
        onSuccess: (data) => {
            if (data.code === 1000) {
                messageApi.success("Sample name updated")
                queryClient.invalidateQueries({ queryKey: ["getorders", "one", sample?.orderId + ''] })
                setEditingName(false)
            } else {
                messageApi.error(data.message || "Failed to update sample name")
            }
        },
        onError: () => {
            messageApi.error("Failed to update sample name")
        },
    })

    const handleSaveName = () => {
        const trimmed = nameValue.trim()
        if (!trimmed) {
            messageApi.error("Sample name cannot be empty")
            return
        }
        if (trimmed === sample?.sampleName) {
            setEditingName(false)
            return
        }
        updateNameMutation.mutate(trimmed)
    }

    const mutation = useMutation({
        mutationFn: (newStatus: SampleStatusType) =>
            updateSampleStatus({ sampleId: sample?.id ?? 0, status: newStatus }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getorders", "one", sample?.orderId+''] })
            // refetch()
            messageApi.success("Sample status updated")
            //  window.location.reload() 
        },
        onError: () => {
            messageApi.error("Failed to update sample status")
        },
    })

    if (!sample) return <></>

    return (
        <>
            {contextHolder}
            <Row>
                <Col span={3}>
                    Sample:{" "}
                    {admin && editingName ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <Input
                                size="small"
                                value={nameValue}
                                onChange={(e) => setNameValue(e.target.value)}
                                onPressEnter={handleSaveName}
                                autoFocus
                                style={{ width: 160 }}
                                disabled={updateNameMutation.isPending}
                            />
                            <Button
                                size="small"
                                type="primary"
                                icon={<CheckOutlined />}
                                loading={updateNameMutation.isPending}
                                onClick={handleSaveName}
                            />
                            <Button
                                size="small"
                                icon={<CloseOutlined />}
                                disabled={updateNameMutation.isPending}
                                onClick={() => { setNameValue(sample?.sampleName ?? ''); setEditingName(false) }}
                            />
                        </span>
                    ) : (
                        <span>
                            <span>{sample.sampleName}</span>
                            {admin && (
                                <Button
                                    size="small"
                                    type="text"
                                    icon={<EditOutlined />}
                                    style={{ marginLeft: 4 }}
                                    onClick={() => setEditingName(true)}
                                />
                            )}
                        </span>
                    )}{" "}
                </Col>
                <Col span={4}>
                    <span style={{ marginLeft: "2em" }}>
                        {" "}
                        SampleID: <span>{sample.id}</span>{" "}
                    </span>
                </Col>
                {!admin && (
                    <Col span={6}>
                        <span style={{ marginLeft: "2em" }}>
                            status:{" "}
                            <Tag style={{ backgroundColor: SAMPLE_STATUS_COLORS[sample.status ?? ""] }}>
                                {SAMPLE_STATUS_DICT[sample.status ?? ""]}
                            </Tag>
                        </span>
                    </Col>
                )}
                {sample.status === "completed" && !admin && (
                    <Col span={4}>
                        <span style={{ marginLeft: "2em" }}>
                            <Checkbox
                                checked={requestReview}
                                onChange={(e) => {
                                    setRequestReview(e.target.checked)
                                    if (e.target.checked) {
                                        mutation.mutate("review-requested")
                                    }
                                }}
                            >
                                Request review
                            </Checkbox>
                        </span>
                    </Col>
                )}

                {!sample.sequence && (
                    <Col span={6}>
                        <span style={{ marginLeft: "3em" }}>No template provided</span>
                    </Col>
                )}
                {admin && (
                    <Col span={6}>
                        <span
                            style={{
                                marginLeft: "2em",
                                display: "inline-block",
                                verticalAlign: "middle",
                                width: "180px",
                            }}
                        >
                            <Dropdown
                                menu={{
                                    items: SAMPLE_STATUS_OPTIONS.map((opt) => ({
                                        key: opt.value,
                                        label: (
                                            <span
                                                style={{
                                                    display: "block",
                                                    backgroundColor:MENU_WITH_COLORS? SAMPLE_STATUS_COLORS[opt.value]:"white",
                                                    color: "#222",
                                                    padding: "4px 12px",
                                                    width: "100%",
                                                }}
                                            >
                                                {SAMPLE_STATUS_DICT[opt.value]}
                                            </span>
                                        ),
                                        style: {
                                            padding: 0,
                                        },
                                    })),
                                    onClick: ({ key }) => {
                                        if (key !== sample.status) {
                                           
                                            mutation.mutate(key as SampleStatusType)
                                        } else {
                                            console.log("Sample is already in this status")
                                        }
                                    },
                                }}
                                trigger={["click"]}
                                disabled={mutation.isPending}
                            >
                                <Button
                                    size="small"
                                    style={{
                                        width: "100%",
                                        minWidth: 0,
                                        backgroundColor: SAMPLE_STATUS_COLORS[sample.status ?? ""],
                                        color: "#222",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        padding: 0,
                                        border: "none",
                                        position: "relative",
                                    }}
                                >
                                    {/* Centered text with right margin for arrow */}
                                    <span
                                        style={{
                                            flex: 1,
                                            textAlign: "center",
                                            pointerEvents: "none",
                                            marginRight: 24, // add space for the arrow
                                            whiteSpace: "nowrap",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                    >
                                        {SAMPLE_STATUS_DICT[sample.status ?? ""]}
                                    </span>
                                    {/* Down arrow absolutely positioned at the right */}
                                    <span
                                        style={{
                                            position: "absolute",
                                            right: 8,
                                            top: "50%",
                                            transform: "translateY(-50%)",
                                            pointerEvents: "none",
                                        }}
                                    >
                                        <DownOutlined />
                                    </span>
                                </Button>
                            </Dropdown>
                        </span>
                    </Col>
                )}
            </Row>
            <Row>
                {sample.sequence && (
                    <>
                        <Row>Sequence:</Row>
                        <Row>
                            <div className="gene-sequence-box">{sample?.sequence ?? ""}</div>
                        </Row>

                        <Row style={{ marginBottom: "2em" }}>
                            {
                                <span style={{ marginRight: "2em" }}>
                                    {sample.fullLength ? <Tag> {"Full length"}</Tag> : <Tag>{"Partial length"}</Tag>}
                                </span>
                            }{" "}
                            Total bases: {nbases}
                        </Row>
                    </>
                )}
            </Row>
        </>
    )
}
